import { makeAutoObservable, runInAction } from 'mobx'

interface User {
  id: string
  isAnonymous: boolean
}

// Check if Supabase is properly configured
const isSupabaseConfigured = () => {
  const url = import.meta.env.VITE_SUPABASE_URL
  return url && url.length > 0 && url !== 'undefined'
}

class AuthStore {
  user: User | null = null
  loading = true
  error: string | null = null

  constructor() {
    makeAutoObservable(this)
    this.init()
  }

  async init() {
    // If Supabase is not configured, use local dev mode
    if (!isSupabaseConfigured()) {
      console.log('[Auth] Supabase not configured, using local mode')
      runInAction(() => {
        this.loading = false
        // Create a local user for development
        const storedUserId = localStorage.getItem('planetmoji_user_id')
        const userId = storedUserId || 'local-' + Math.random().toString(36).substr(2, 9)
        if (!storedUserId) {
          localStorage.setItem('planetmoji_user_id', userId)
        }
        this.user = {
          id: userId,
          isAnonymous: true
        }
        console.log('[Auth] Local user:', userId)
      })
      return
    }

    // Supabase is configured - use it
    console.log('[Auth] Supabase configured, attempting auth...')
    try {
      const { signInAnonymously, getCurrentUser, onAuthStateChange } = await import('../lib/supabase')
      
      const currentUser = await getCurrentUser()
      console.log('[Auth] Current user:', currentUser?.id || 'none')
      
      if (currentUser) {
        runInAction(() => {
          this.user = {
            id: currentUser.id,
            isAnonymous: currentUser.is_anonymous || false
          }
          this.loading = false
        })
        console.log('[Auth] Logged in as existing user:', currentUser.id)
      } else {
        console.log('[Auth] No user found, signing in anonymously...')
        const { user, error } = await signInAnonymously()
        if (error) {
          console.error('[Auth] Anonymous sign-in error:', error)
          // Fallback: usar usuario local persistente cuando anonymous sign-in está deshabilitado
          runInAction(() => {
            const storedUserId = localStorage.getItem('planetmoji_user_id')
            const localId = storedUserId || 'local-' + Math.random().toString(36).substr(2, 9)
            if (!storedUserId) {
              localStorage.setItem('planetmoji_user_id', localId)
            }
            this.user = {
              id: localId,
              isAnonymous: true
            }
            this.loading = false
            console.log('[Auth] Fallback to local user due to auth error:', localId)
          })
          return
        }
        runInAction(() => {
          if (user) {
            this.user = {
              id: user.id,
              isAnonymous: true
            }
            console.log('[Auth] Signed in anonymously:', user.id)
          }
          this.loading = false
        })
      }

      onAuthStateChange((user: any) => {
        console.log('[Auth] Auth state changed:', user?.id || 'signed out')
        runInAction(() => {
          if (user) {
            this.user = {
              id: user.id,
              isAnonymous: user.is_anonymous || false
            }
          } else {
            this.user = null
          }
        })
      })
    } catch (e) {
      console.error('[Auth] Init error:', e)
      runInAction(() => {
        this.loading = false
        // Fallback to local user
        this.user = {
          id: 'local-fallback-' + Math.random().toString(36).substr(2, 9),
          isAnonymous: true
        }
        console.log('[Auth] Fallback to local user')
      })
    }
  }

  get isAuthenticated() {
    return this.user !== null
  }

  get userId() {
    return this.user?.id || null
  }
}

export const authStore = new AuthStore()
